import { Alert, Form, Select, Spin, Table } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { selectCurrentBusiness } from "../../../businessesSlice";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  getPartnersThunk,
  selectPartners,
} from "../../../../partners/partnersSlice";
import Loader from "../../../../common/loader/Loader";
import {
  IPartner,
  IPurchPriceProfile,
  OnlinePartnerBucketGroups,
  PricingTemplate,
} from "../../../../../interfaces/partner.interface";
import { BucketName } from "../../../../../interfaces/businessFull.interface";
import {
  OnlinePricingTemplate,
  PartnerBucketGroupPrice,
} from "../../../../../interfaces/partnerDeal.interface";
import BucketGroupValue from "../../../../partner-deals/components/partner-deals-table/subcomponents/bucket-group-value/BucketGroupValue";
import PartnerSelector from "../../../../partner-deals/components/partner-selector/PartnerSelector";
import { getPartnerWithPricing } from "../../../../partners/partnersApi";
import {
  getMerchantPartnerOnlinePricingPreviewFromServer,
  PartnerOnlinePricingPreview,
} from "../../../businessesApi";
import {
  isMerchantSubmittedToAdyen,
  isPartnerRemapLocked,
  PARTNER_REMAP_HINT,
} from "../../../utils/isNoEdit";
import { DEFAULT_AMEX_BUCKET } from "../../../../common/constants/defaultAmexBucket";
import {
  buildInPersonComparison,
  formatValue,
  isComparable,
  sixteenBucketOnlineBandFor,
} from "./partnerBands";
import { selectHideInPersonAmex } from "../../../../feature-flags/featureFlagsSlice";
import { resolveCountryAlpha2 } from "../../../utils/resolveCountryAlpha2";
import {
  ONLINE_BUCKET_LABELS,
  getOnlineBucketKeysForTemplate,
  getOnlinePartnerGroupKeys,
  onlinePricingTemplateForCountry,
  type OnlineBucketKey,
} from "../../../../partners/constants/onlineBuckets";
import { OnlinePricingStructure } from "../../../../../interfaces/onlinePricing.interface";
import {
  coverageHasBuckets,
  isKnownOnlinePricingStructure,
  onlineCardSplitHeading,
  onlineGroupLabel,
  resolveEditorOnlinePricingStructure,
  sixteenBucketGroups,
} from "../../../utils/sixteenBucketOnlinePricing";
import {
  onlineGroupBucketSpread,
  resolveOnlineBucketRates,
} from "../../../utils/onlineBucketRates";

const partnerFieldLayout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 12 },
};

function bucketOrderForCountry(
  country?: string
): Array<{ key: OnlineBucketKey; label: string }> {
  return getOnlineBucketKeysForTemplate(
    onlinePricingTemplateForCountry(resolveCountryAlpha2(country))
  ).map((key) => ({ key, label: ONLINE_BUCKET_LABELS[key] }));
}

// Union the resolved template's keys with whatever keys the stored data
// actually has a value under. Filtering strictly by template would hide a fee
// recorded under the other local method (e.g. a BE partner/merchant still on
// iDeal — precisely the legacy state this class of fix exists to surface, not
// hide) instead of showing it as the stale-but-live fee it actually is.
function bucketOrderForData(
  country: string | undefined,
  data: Partial<Record<OnlineBucketKey, unknown>> | undefined,
  template?: OnlinePricingTemplate
): Array<{ key: OnlineBucketKey; label: string }> {
  const templateOrder = template
    ? getOnlineBucketKeysForTemplate(template).map((key) => ({
        key,
        label: ONLINE_BUCKET_LABELS[key],
      }))
    : bucketOrderForCountry(country);
  const templateKeys = new Set(templateOrder.map((row) => row.key));
  const extra = (Object.keys(data ?? {}) as OnlineBucketKey[])
    .filter((key) => !templateKeys.has(key) && data?.[key] != null)
    .map((key) => ({ key, label: ONLINE_BUCKET_LABELS[key] }));
  return [...templateOrder, ...extra];
}

// Each section tags the country the listed buckets are labelled for.
function CountryTag({ country }: { country?: string }) {
  const resolved = resolveCountryAlpha2(country);
  return (
    <span
      style={{ fontSize: 11, fontWeight: 400, opacity: 0.5, marginLeft: 8 }}
    >
      ({resolved ? resolved.toUpperCase() : "country unknown"})
    </span>
  );
}

const PREVIEW_EMPTY_STATE_MESSAGE: Record<
  Exclude<
    PartnerOnlinePricingPreview["status"],
    "ok" | "merchant_submitted_to_adyen"
  >,
  { type: "info" | "warning" | "error"; message: string }
> = {
  missing_brn: {
    type: "warning",
    message:
      "BRN is required to assign a partner. Add the Business Registration Number on Company Profile and retry.",
  },
  partner_not_found: {
    type: "error",
    message: "Selected partner could not be found.",
  },
  partner_has_no_online_pricing: {
    type: "warning",
    message: "This partner has no online pricing configured.",
  },
  partner_range_missing_local_method: {
    type: "warning",
    message:
      "This partner's online range does not price the merchant's local payment method.",
  },
  klearly_cut_not_configured: {
    type: "warning",
    message: "Klearly cut is not configured for this partner's online pricing.",
  },
};

type OnlinePricingRow = {
  key: string;
  label: string;
  currentValue: string | null;
  klearlyMarkup: string | null;
  partnerMarkup: string | null;
};

export default function PartnerMapper() {
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const hideInPersonAmex = useAppSelector(selectHideInPersonAmex);
  const partners = useAppSelector(selectPartners);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>();
  const [selectedPartner, setSelectedPartner] = useState<IPartner>();
  const [purchPriceProfiles, setPurchPriceProfiles] =
    useState<IPurchPriceProfile[]>();
  const [selectedPurchProfile, setSelectedPurchProfile] =
    useState<IPurchPriceProfile>();
  const [preview, setPreview] = useState<PartnerOnlinePricingPreview>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewRequestIdRef = useRef(0);

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  // The read fills adyen.onboardingStatus with "onboardingAllowed" for
  // merchants that have never been submitted. A stored split config, or a
  // frozen partner / online-fee path, is the signal that they are live.
  const isAdyenSubmitted = isMerchantSubmittedToAdyen(currentBusiness);
  const partnerRemapLocked = isPartnerRemapLocked(currentBusiness);

  useEffect(() => {
    setSelectedPartnerId(undefined);
    setSelectedPartner(undefined);
    setPurchPriceProfiles(undefined);
    setSelectedPurchProfile(undefined);
    setPreview(undefined);
    setPreviewLoading(false);
    if (!partners || partners?.length === 0) {
      getAllPartners();
    }
    if (currentBusiness?.partnerId) {
      let partner = partners.find(
        //@ts-ignore
        (p) => p._id === currentBusiness.partnerId?._id
      );
      setSelectedPartnerId(partner?._id);
      if (partner?._id && !isAdyenSubmitted) setPreviewLoading(true);
      setSelectedPartner(partner);
      setPurchPriceProfiles(partner?.purchPriceProfiles);
      if (currentBusiness.partnerPurchPriceProfileId) {
        let profile = partner?.purchPriceProfiles.find(
          (pr) => pr._id === currentBusiness.partnerPurchPriceProfileId
        );
        setSelectedPurchProfile(profile);
      }
    }
  }, [currentBusiness]);

  // Ensure selected partner and profiles are loaded after partners fetch or page refresh
  useEffect(() => {
    const syncFromBusiness = async () => {
      const businessPartnerId = (currentBusiness?.partnerId as any)?._id;
      if (!businessPartnerId) return;

      let partner = partners.find((p) => p._id === businessPartnerId);
      if (
        !partner ||
        !Array.isArray((partner as any)?.purchPriceProfiles) ||
        (partner as any)?.purchPriceProfiles?.length === 0 ||
        !(partner as any)?.purchPriceProfiles?.some(
          (profile: { inPersonBucketGroupCoverage?: unknown[] }) =>
            profile.inPersonBucketGroupCoverage?.length
        )
      ) {
        try {
          partner = (await getPartnerWithPricing(businessPartnerId)) || partner;
        } catch {}
      }
      if (!partner) return;

      setSelectedPartnerId(businessPartnerId);
      setSelectedPartner(partner);
      setPurchPriceProfiles(partner?.purchPriceProfiles);

      const preselectedId = (currentBusiness as any)
        ?.partnerPurchPriceProfileId;
      let profile = partner?.purchPriceProfiles?.find(
        (pr) => pr._id === preselectedId
      );
      if (!profile)
        profile = partner?.purchPriceProfiles?.find(
          (pr) => pr.name === "default"
        );
      setSelectedPurchProfile(profile);
    };

    syncFromBusiness();
  }, [
    partners,
    currentBusiness?.partnerId,
    currentBusiness?.partnerPurchPriceProfileId,
  ]);

  // Online pricing preview: refetch whenever selected partner or business changes.
  // AbortController cancels superseded requests; request id drops any response
  // that races past the abort (e.g. BE replied before fetch checked the signal).
  useEffect(() => {
    if (!currentBusiness?._id || !selectedPartnerId || isAdyenSubmitted) {
      setPreview(undefined);
      setPreviewLoading(false);
      return;
    }
    const requestId = ++previewRequestIdRef.current;
    const controller = new AbortController();
    setPreviewLoading(true);
    getMerchantPartnerOnlinePricingPreviewFromServer({
      businessId: currentBusiness._id,
      partnerId: selectedPartnerId,
      signal: controller.signal,
    })
      .then((result) => {
        if (requestId !== previewRequestIdRef.current) return;
        setPreview(result);
      })
      .catch(() => {
        if (requestId !== previewRequestIdRef.current) return;
        setPreview(undefined);
      })
      .finally(() => {
        if (requestId === previewRequestIdRef.current) setPreviewLoading(false);
      });
    return () => controller.abort();
  }, [selectedPartnerId, currentBusiness?._id, isAdyenSubmitted]);

  const getAllPartners = async () => {
    setIsLoading(true);
    await dispatch(getPartnersThunk());
    setIsLoading(false);
  };

  const handlePartnerSelection = async (partnerId: string) => {
    if (partnerRemapLocked) return;
    setSelectedPartnerId(partnerId);
    setPreview(undefined);
    setPreviewLoading(true);
    let partner = partners.find((p) => p._id === partnerId);
    try {
      if (
        !partner ||
        !Array.isArray((partner as any)?.purchPriceProfiles) ||
        (partner as any)?.purchPriceProfiles?.length === 0 ||
        !(partner as any)?.purchPriceProfiles?.some(
          (profile: { inPersonBucketGroupCoverage?: unknown[] }) =>
            profile.inPersonBucketGroupCoverage?.length
        )
      ) {
        partner = (await getPartnerWithPricing(partnerId)) || partner;
      }
    } catch (e) {}

    setSelectedPartner(partner);
    setPurchPriceProfiles((partner as any)?.purchPriceProfiles);
    const defaultPricing =
      (partner as any)?.purchPriceProfiles?.find(
        (pr: any) => pr.name === "default"
      ) || (partner as any)?.purchPriceProfiles?.[0];
    setSelectedPurchProfile(defaultPricing);
  };

  const handlePurchPriceSelection = (purchPriceProfileId: string) => {
    let purchPriceProfile = purchPriceProfiles?.find(
      (p) => p._id === purchPriceProfileId
    );
    if (purchPriceProfile) setSelectedPurchProfile(purchPriceProfile);
  };

  const merchantBucketRanges = useMemo(() => {
    if (!selectedPurchProfile) return undefined;
    const rows = buildInPersonComparison(
      currentBusiness?.merchantServiceChargeFees,
      selectedPurchProfile
    );
    if (!hideInPersonAmex) return rows;
    return rows.filter((r) => r.name !== BucketName.InPersonAmex);
  }, [
    selectedPurchProfile,
    currentBusiness?.merchantServiceChargeFees,
    hideInPersonAmex,
  ]);

  const incomparableRows =
    merchantBucketRanges?.filter((r) => !r.comparable) ?? [];

  const editorOnlinePricingStructure = useMemo(
    () =>
      resolveEditorOnlinePricingStructure({
        business: currentBusiness,
        previewStructure: preview?.pricing?.onlinePricingStructure,
        mappedPartner: true,
      }),
    [currentBusiness, preview?.pricing?.onlinePricingStructure]
  );

  const onlinePricingRows = useMemo<OnlinePricingRow[] | undefined>(() => {
    if (preview?.status !== "ok" || !preview.pricing?.onlinePricing) {
      return undefined;
    }
    const klearlyPricing = preview.pricing.onlinePricing;
    const currentPricing = currentBusiness?.onlinePricing;
    const structure = editorOnlinePricingStructure;

    if (structure === OnlinePricingStructure.SixteenBucket) {
      const coverage = preview.pricing.onlineBucketGroupCoverage;
      const groups = sixteenBucketGroups(currentBusiness, coverage);
      const rates = resolveOnlineBucketRates({
        groups,
        coverage,
        storedFees: currentBusiness?.onlineMerchantServiceChargeFees,
        groupRates: Object.fromEntries(
          groups.map((group) => {
            const stored = currentPricing?.[group.key];
            const previewGroup = klearlyPricing[group.key];
            return [
              group.key,
              {
                value:
                  typeof stored?.value === "number"
                    ? stored.value
                    : (previewGroup?.value ?? null),
                valueType: (stored?.valueType ??
                  previewGroup?.valueType ??
                  "Percentage") as "Fixed" | "Percentage",
              },
            ];
          })
        ),
      });
      return groups.flatMap((group) => {
        const band = sixteenBucketOnlineBandFor(group.key, {
          previewPricing: klearlyPricing,
          hasPartner: true,
        });
        const current =
          currentPricing?.[group.key] ?? klearlyPricing[group.key];
        const groupRow: OnlinePricingRow = {
          key: group.key,
          label: group.label,
          currentValue:
            current && typeof current.value === "number"
              ? formatValue(current.value, current.valueType)
              : null,
          klearlyMarkup:
            band && typeof band.min === "number"
              ? formatValue(band.min, band.valueType)
              : null,
          partnerMarkup:
            current &&
            typeof current.value === "number" &&
            band &&
            typeof band.min === "number" &&
            isComparable(current.valueType, band.valueType)
              ? formatValue(current.value - band.min, current.valueType)
              : null,
        };
        // The group rate is not what the merchant is charged once a bucket is
        // priced away from it, so a diverged group lists its buckets. The band
        // is the group's, so it is not repeated on them.
        const spread = onlineGroupBucketSpread(group, rates);
        if (!spread?.diverged) return [groupRow];
        return [
          groupRow,
          ...group.buckets.map((bucket) => {
            const rate = rates[bucket.field];
            return {
              key: bucket.field,
              label: `· ${bucket.label}`,
              currentValue: rate
                ? formatValue(rate.value, rate.valueType)
                : null,
              klearlyMarkup: null,
              partnerMarkup: null,
            };
          }),
        ];
      });
    }

    const sourcePricing = currentPricing ?? klearlyPricing;
    return bucketOrderForData(
      currentBusiness?.address?.country,
      sourcePricing,
      preview.pricing.onlinePricingTemplate as OnlinePricingTemplate | undefined
    ).map(({ key, label }) => {
      const k = klearlyPricing[key];
      const current = sourcePricing[key];
      const currentValue =
        current && typeof current.value === "number"
          ? formatValue(current.value, current.valueType)
          : null;
      const klearlyMarkup =
        k && typeof k.min === "number"
          ? formatValue(k.min, k.bandValueType ?? k.valueType)
          : null;
      const partnerMarkup =
        current &&
        typeof current.value === "number" &&
        k &&
        typeof k.min === "number" &&
        current.valueType === (k.bandValueType ?? k.valueType)
          ? formatValue(current.value - k.min, k.bandValueType ?? k.valueType)
          : null;
      return {
        key,
        label,
        currentValue,
        klearlyMarkup,
        partnerMarkup,
      };
    });
  }, [preview, currentBusiness, editorOnlinePricingStructure]);

  if (isAdyenSubmitted) {
    return (
      <>
        <Form.Item
          label="Partner"
          wrapperCol={{ ...partnerFieldLayout.wrapperCol }}
          labelCol={{ ...partnerFieldLayout.labelCol }}
          labelAlign="left"
        >
          <PartnerSelector
            value={selectedPartnerId}
            disabled
            allowClear={false}
            disabledHint={PARTNER_REMAP_HINT}
            onChange={() => {}}
          />
        </Form.Item>
        <Alert
          type="info"
          showIcon
          message={
            currentBusiness?.partnerId
              ? "A super admin must remove the current partner through Sensitive Actions before selecting a replacement."
              : "Mapping direct merchants submitted to Adyen to Partners is not supported yet."
          }
        />
      </>
    );
  }

  return (
    <>
      {!isLoading && (
        <>
          <Form.Item
            label="Partner"
            name={["partnerId"]}
            wrapperCol={{ ...partnerFieldLayout.wrapperCol }}
            labelCol={{ ...partnerFieldLayout.labelCol }}
            labelAlign="left"
            validateStatus={
              preview?.status === "missing_brn" ? "error" : undefined
            }
            help={
              preview?.status === "missing_brn"
                ? PREVIEW_EMPTY_STATE_MESSAGE.missing_brn.message
                : undefined
            }
            rules={[
              {
                validator: async () => {
                  if (preview?.status === "missing_brn") {
                    return Promise.reject(
                      new Error(PREVIEW_EMPTY_STATE_MESSAGE.missing_brn.message)
                    );
                  }
                },
              },
            ]}
          >
            <PartnerSelector
              value={selectedPartnerId}
              disabled={partnerRemapLocked}
              disabledHint={partnerRemapLocked ? PARTNER_REMAP_HINT : undefined}
              onChange={handlePartnerSelection}
              allowClear={false}
            />
          </Form.Item>
          {(selectedPartnerId ||
            currentBusiness?.partnerPurchPriceProfileId) && (
            <Form.Item
              label="Pricing Profile"
              name={["partnerPurchPriceProfileId"]}
              wrapperCol={{ ...partnerFieldLayout.wrapperCol }}
              labelCol={{ ...partnerFieldLayout.labelCol }}
              labelAlign="left"
              rules={[
                {
                  validator: async () => {
                    if (
                      merchantBucketRanges &&
                      merchantBucketRanges.findIndex((r) => !r.isInRange) >= 0
                    )
                      return Promise.reject(
                        new Error(`"Pricing is out of range!`)
                      );
                  },
                },
              ]}
            >
              <Select
                style={{ width: 200 }}
                onChange={handlePurchPriceSelection}
                value={selectedPurchProfile?._id}
                options={purchPriceProfiles?.map((p) => ({
                  label: p.name,
                  value: p._id,
                  key: p._id,
                }))}
              />
            </Form.Item>
          )}

          {selectedPartnerId && (
            <div>
              <h3>Partner Profile Range</h3>
              <br />
              {selectedPurchProfile?._id && (
                <>
                  <h4 style={{ opacity: 0.6, marginTop: 0 }}>IN PERSON</h4>
                  <BucketGroupValue
                    label="Domestic Debit Card"
                    value={
                      selectedPurchProfile.bucketGroups.domesticDebitCards.value
                    }
                    max={
                      selectedPurchProfile.bucketGroups.domesticDebitCards.max
                    }
                    valueType={
                      selectedPurchProfile.bucketGroups.domesticDebitCards
                        .valueType
                    }
                  />
                  {selectedPurchProfile.pricingTemplate ===
                    PricingTemplate.EuropeanDebitSplit && (
                    <BucketGroupValue
                      label="European Debit"
                      value={
                        selectedPurchProfile.bucketGroups.europeanDebitCards
                          ?.value || 0
                      }
                      max={
                        selectedPurchProfile.bucketGroups.europeanDebitCards
                          ?.max || 0
                      }
                      valueType={
                        selectedPurchProfile.bucketGroups.europeanDebitCards
                          ?.valueType || "Fixed"
                      }
                    />
                  )}
                  {selectedPurchProfile.pricingTemplate ===
                    PricingTemplate.DomesticCreditSplit && (
                    <BucketGroupValue
                      label="Domestic Credit"
                      value={
                        selectedPurchProfile.bucketGroups.domesticCreditCards
                          ?.value || 0
                      }
                      max={
                        selectedPurchProfile.bucketGroups.domesticCreditCards
                          ?.max || 0
                      }
                      valueType={
                        selectedPurchProfile.bucketGroups.domesticCreditCards
                          ?.valueType || "Fixed"
                      }
                    />
                  )}
                  <BucketGroupValue
                    label="European"
                    value={selectedPurchProfile.bucketGroups.european.value}
                    max={selectedPurchProfile.bucketGroups.european.max}
                    valueType={
                      selectedPurchProfile.bucketGroups.european.valueType
                    }
                  />
                  <BucketGroupValue
                    label="International & Commericial"
                    value={
                      selectedPurchProfile.bucketGroups
                        .internationalAndCommercial.value
                    }
                    max={
                      selectedPurchProfile.bucketGroups
                        .internationalAndCommercial.max
                    }
                    valueType={
                      selectedPurchProfile.bucketGroups
                        .internationalAndCommercial.valueType
                    }
                  />
                  {/* PC-926: in-person AMEX only when hideInPersonAmex is off. */}
                  {!hideInPersonAmex && (
                    <BucketGroupValue
                      label="AMEX"
                      value={
                        selectedPurchProfile.bucketGroups.amex?.valueType ===
                        "Percentage"
                          ? selectedPurchProfile.bucketGroups.amex.value
                          : DEFAULT_AMEX_BUCKET.value
                      }
                      max={
                        selectedPurchProfile.bucketGroups.amex?.valueType ===
                        "Percentage"
                          ? selectedPurchProfile.bucketGroups.amex.max
                          : DEFAULT_AMEX_BUCKET.max
                      }
                      valueType="Percentage"
                    />
                  )}
                </>
              )}

              <OnlinePartnerProfileRange
                country={currentBusiness?.address?.country}
                preview={preview}
                loading={previewLoading}
              />

              <br />
              <br />
              {selectedPurchProfile?._id && (
                <>
                  <h3>Merchant Current In Person Pricing</h3>
                  {merchantBucketRanges &&
                    merchantBucketRanges.findIndex((r) => !r.isInRange) >=
                      0 && (
                      <>
                        <span
                          style={{
                            color: "red",
                            fontWeight: 600,
                          }}
                        >
                          Pricing is not in range ! Please adjust pricing both
                          in Rapyd and in Klearly.
                        </span>
                        <br />
                      </>
                    )}
                  {incomparableRows.length > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 8 }}
                      message={`Not comparable: ${incomparableRows
                        .map((r) => r.name)
                        .join(
                          ", "
                        )}. The merchant fee and this profile's bucket use different units, so no Klearly cut or partner markup can be derived.`}
                    />
                  )}
                  <br />

                  <Table
                    dataSource={merchantBucketRanges}
                    rowKey="name"
                    pagination={false}
                    columns={[
                      {
                        title: "",
                        dataIndex: "name",
                        render: (v, record) => (
                          <span
                            style={{
                              color: record?.isInRange ? "inherit" : "red",
                            }}
                          >
                            {v}
                          </span>
                        ),
                      },
                      {
                        title: "current",
                        dataIndex: "currentValue",
                        render: (v, record) => (
                          <span
                            style={{
                              color: record?.isInRange ? "inherit" : "red",
                            }}
                          >
                            {v}
                          </span>
                        ),
                      },
                      {
                        title: "K",
                        dataIndex: "klearlyMarkup",
                        render: (v, record) =>
                          record?.comparable ? v : "not comparable",
                      },
                      {
                        title: "P",
                        dataIndex: "partnerMarkup",
                        render: (v, record) => (record?.comparable ? v : "—"),
                      },
                    ]}
                  />
                  <br />
                </>
              )}

              <h3>
                Merchant Current Online Pricing
                <CountryTag country={currentBusiness?.address?.country} />
              </h3>
              {preview?.status === "ok" &&
                editorOnlinePricingStructure ===
                  OnlinePricingStructure.SixteenBucket && (
                  <div style={{ opacity: 0.6, marginBottom: 8, fontSize: 13 }}>
                    {onlineCardSplitHeading(
                      preview.pricing?.onlineCardSplitTemplate
                    )}
                  </div>
                )}
              <OnlinePricingPreviewSection
                preview={preview}
                loading={previewLoading}
                rows={onlinePricingRows}
              />
              <br />
            </div>
          )}
        </>
      )}

      {isLoading && <Loader text="Getting All Partners" />}
    </>
  );
}

function OnlinePartnerProfileRange({
  country,
  preview,
  loading,
}: {
  country?: string;
  preview?: PartnerOnlinePricingPreview;
  loading: boolean;
}) {
  const header = (
    <>
      <br />
      <h4 style={{ opacity: 0.6, marginTop: 0 }}>
        ONLINE
        <CountryTag country={country} />
      </h4>
    </>
  );

  if (loading) {
    return (
      <>
        {header}
        <div style={{ padding: "8px 0" }}>
          <Spin size="small" />{" "}
          <span style={{ marginLeft: 8 }}>Loading online pricing preview…</span>
        </div>
      </>
    );
  }

  // A missing preview is not a missing range; only an explicit status is.
  if (!preview) {
    return header;
  }

  if (preview.status !== "ok") {
    if (preview.status === "merchant_submitted_to_adyen") return header;
    const empty = PREVIEW_EMPTY_STATE_MESSAGE[preview.status];
    if (!empty) return header;
    return (
      <>
        {header}
        <div style={{ opacity: 0.6 }}>{empty.message}</div>
      </>
    );
  }

  // Same bands the merchant-current table is judged against — not the
  // partner's top-level default, which can be a different structure.
  const pricing = preview?.pricing?.onlinePricing as
    OnlinePartnerBucketGroups | undefined;
  if (!pricing) {
    return (
      <>
        {header}
        <div style={{ opacity: 0.6 }}>
          This partner has no online pricing configured.
        </div>
      </>
    );
  }

  const declared = preview?.pricing?.onlinePricingStructure;
  const structure = isKnownOnlinePricingStructure(declared)
    ? declared
    : coverageHasBuckets(preview?.pricing?.onlineBucketGroupCoverage)
      ? OnlinePricingStructure.SixteenBucket
      : OnlinePricingStructure.FourBucket;
  const template =
    (preview?.pricing?.onlinePricingTemplate as
      OnlinePricingTemplate | undefined) ??
    onlinePricingTemplateForCountry(resolveCountryAlpha2(country));
  const rows =
    structure === OnlinePricingStructure.SixteenBucket
      ? getOnlinePartnerGroupKeys({
          structure: OnlinePricingStructure.SixteenBucket,
          template,
          coverage: preview?.pricing?.onlineBucketGroupCoverage,
          stored: pricing,
        }).map((key) => ({ key, label: onlineGroupLabel(key) }))
      : bucketOrderForData(country, pricing, template);

  return (
    <>
      {header}
      {rows.map(({ key, label }) => {
        const bucket = pricing[key] as PartnerBucketGroupPrice | undefined;
        if (!bucket) {
          if (structure === OnlinePricingStructure.SixteenBucket) {
            return null;
          }
          return (
            <div key={key} style={{ opacity: 0.6 }}>
              {label}: not configured
            </div>
          );
        }
        return (
          <BucketGroupValue
            key={key}
            label={label}
            value={bucket.min ?? bucket.value}
            max={bucket.max}
            valueType={bucket.bandValueType ?? bucket.valueType}
          />
        );
      })}
    </>
  );
}

function OnlinePricingPreviewSection({
  preview,
  loading,
  rows,
}: {
  preview: PartnerOnlinePricingPreview | undefined;
  loading: boolean;
  rows: OnlinePricingRow[] | undefined;
}) {
  if (loading) {
    return (
      <div style={{ padding: "16px 0" }}>
        <Spin size="small" />{" "}
        <span style={{ marginLeft: 8 }}>Loading online pricing preview…</span>
      </div>
    );
  }
  if (!preview) {
    return null;
  }
  switch (preview.status) {
    case "ok":
      return rows ? (
        <Table
          dataSource={rows}
          rowKey="key"
          pagination={false}
          columns={[
            { title: "", dataIndex: "label" },
            { title: "current", dataIndex: "currentValue" },
            { title: "K", dataIndex: "klearlyMarkup" },
            { title: "P", dataIndex: "partnerMarkup" },
          ]}
        />
      ) : null;
    case "merchant_submitted_to_adyen":
      // Top-level lock handles this state; section is unreachable here.
      return null;
    case "missing_brn":
    case "partner_not_found":
    case "partner_has_no_online_pricing":
    case "partner_range_missing_local_method":
    case "klearly_cut_not_configured": {
      const { type, message } = PREVIEW_EMPTY_STATE_MESSAGE[preview.status];
      return <Alert type={type} showIcon message={message} />;
    }
    default: {
      const _exhaustive: never = preview.status;
      return _exhaustive;
    }
  }
}
